import { useState, useEffect } from "react"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { Toaster } from "sonner"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar"
import { TooltipProvider } from "@/components/ui/tooltip"
import { Separator } from "@/components/ui/separator"
import { CliSidebar } from "@/components/CliSidebar"
import { AdminSidebar } from "@/components/AdminSidebar"
import { CliSettings } from "@/pages/cli/Settings"
import type { SkuRule, SkuPrefix } from "@/pages/cli/Settings"
import { NewBatchPage } from "@/pages/cli/NewBatch"
import { JobsView } from "@/pages/cli/Jobs"
import { CataloguePage } from "@/pages/cli/Catalogue"
import { Login } from "@/pages/Login"
import { AdminDashboard } from "@/pages/admin/Dashboard"
import { ClientsView, ClientDetail, NewClientForm } from "@/pages/admin/Clients"
import { APISettings } from "@/pages/admin/APISettings"
import { VPSMonitor } from "@/pages/admin/VPSMonitor"
import { AvatarsPage } from "@/pages/admin/Avatars"
import { AdminUsersPage } from "@/pages/admin/AdminUsers"
import { ModulesPage } from "@/pages/admin/Modules"
import { DebugPage } from "@/pages/admin/Debug"
import { type Client, type AvatarFolder, type AvatarAsset, type Module } from "@/data/adminData"
import { jobsApi, cloudApi, settingsApi, adminClientsApi, adminAvatarsApi, adminUsersApi, adminModulesApi, cliModulesApi, cliAvatarsApi, fetchMaintenanceStatus, ApiError, clearTokens, getStoredRole, getStoredUser, type JobDTO, type CloudStatusDTO, type TenantDTO, type AvatarFolderDTO, type AvatarAssetDTO, type AdminUserDTO } from "@/lib/api"
import { MaintenancePage } from "@/pages/cli/MaintenancePage"
import { Button } from "@/components/ui/button"

type Page = "login" | "cli" | "admin"

// ── CLI ───────────────────────────────────────────────────────────────────
export type CloudConn = {
  google:  { connected: boolean; email: string | null }
  dropbox: { connected: boolean; email: string | null }
}
const CLOUD_INIT: CloudConn = {
  google:  { connected: false, email: null },
  dropbox: { connected: false, email: null },
}

export type Job = {
  id: string
  name: string
  source: 'drive' | 'dropbox' | 'local'
  fileCount: number
  skuCount: number
  status: 'done' | 'partial' | 'processing' | 'batching' | 'error' | 'not_configured' | 'not_started' | 'queued' | 'paused' | 'cancelled'
  progress?: number
  createdAt: string
  sourceUrl?: string
  outputFormat?: 'jpeg' | 'png' | 'webp'
  outputResolution?: '1080x1920' | '1440x2560' | '2160x3840'
  namingConvention?: 'sku' | 'marque-sku' | 'custom'
  namingCustom?: string
}

export type AvatarOption = { id: string; name: string; img: string; folder_id: number | null; folder_name: string | null }

function dtoToJob(dto: JobDTO): Job {
  return {
    id: String(dto.id),
    name: dto.name,
    source: dto.source === "drive" ? "drive" : dto.source === "dropbox" ? "dropbox" : "local",
    fileCount: dto.file_count,
    skuCount: dto.sku_count,
    status: dto.status,
    progress: dto.progress ?? undefined,
    createdAt: dto.created_at,
    sourceUrl: dto.source_url ?? undefined,
    outputFormat: dto.output_format ?? undefined,
    outputResolution: (dto.output_resolution as Job["outputResolution"]) ?? undefined,
    namingConvention: (dto.naming_convention as Job["namingConvention"]) ?? undefined,
    namingCustom: dto.naming_custom ?? undefined,
  }
}

function cloudStatusToConn(cs: CloudStatusDTO): CloudConn {
  return {
    google: { connected: cs.google_connected, email: cs.google_email },
    dropbox: { connected: cs.dropbox_connected, email: cs.dropbox_email },
  }
}

function CliApp({ onBack }: { onBack: () => void }) {
  const [view, setView] = useState(() => localStorage.getItem("cli_view") ?? "batches")
  const [selectedCatalogueId, setSelectedCatalogueId] = useState<number | undefined>(undefined)
  const navTo = (v: string) => { setView(v); localStorage.setItem("cli_view", v) }
  const navToCatalogue = (catId?: number) => { setSelectedCatalogueId(catId); navTo('catalogue') }
  const [availableAvatars, setAvailableAvatars] = useState<AvatarOption[]>([])
  const [bgProgress, setBgProgress] = useState<number | null>(null)
  const [cloud, setCloud] = useState<CloudConn>(CLOUD_INIT)
  const [jobs, setJobs] = useState<Job[]>([])
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [skuProperties] = useState<string[]>(['SEX', 'AGE'])
  const [skuRules, setSkuRules] = useState<SkuRule[]>([])
  const [enabledModules, setEnabledModules] = useState<string[] | undefined>(undefined)
  const [credits, setCredits] = useState<{ budget: number; used: number } | null>(null)
  const [maintenanceMode, setMaintenanceMode] = useState(
    () => localStorage.getItem("maintenance_mode") === "true"
  )

  const applyMaintenance = (on: boolean) => {
    setMaintenanceMode(on)
    if (on) localStorage.setItem("maintenance_mode", "true")
    else localStorage.removeItem("maintenance_mode")
  }

  const handleApiError = (e: unknown) => {
    if (e instanceof ApiError && e.status === 503) applyMaintenance(true)
  }

  // Re-fetch jobs whenever the user navigates to the Jobs list — surfaces ghosts and cross-tab changes
  useEffect(() => {
    if (view === 'batches' || view === 'batches-done') {
      jobsApi.list().then(dtos => setJobs(dtos.map(dtoToJob))).catch(() => {})
    }
  }, [view])  // eslint-disable-line react-hooks/exhaustive-deps

  // Poll job list every 10s while any job is active — keeps status in sync if SSE drops
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'processing' || j.status === 'queued')
    if (!hasActive) return
    const id = setInterval(() => {
      jobsApi.list().then(dtos => setJobs(dtos.map(dtoToJob))).catch(() => {})
    }, 10_000)
    return () => clearInterval(id)
  }, [jobs.map(j => j.status).join(',')])  // eslint-disable-line react-hooks/exhaustive-deps

  // Load jobs and cloud status from API on mount
  useEffect(() => {
    fetchMaintenanceStatus().then(enabled => applyMaintenance(enabled))
    jobsApi.list().then(dtos => setJobs(dtos.map(dtoToJob))).catch(handleApiError)
    cloudApi.status().then(cs => setCloud(cloudStatusToConn(cs))).catch(handleApiError)
    cliModulesApi.list().then(mods => setEnabledModules(mods.filter(m => m.enabled).map(m => m.module_id))).catch(handleApiError)
    cliAvatarsApi.list().then(dtos => setAvailableAvatars(dtos.map(d => ({ id: String(d.id), name: d.name, img: d.img_path, folder_id: d.folder_id, folder_name: d.folder_name })))).catch(handleApiError)
    settingsApi.get().then(s => {
      if (s.studio_api) setCredits({ budget: s.credits_budget, used: s.credits_used })
      const grouped = new Map<string, SkuPrefix[]>()
      for (const r of s.sku_rules) {
        const existing = grouped.get(r.avatar_id) ?? []
        existing.push({ value: r.prefix, dbId: r.id })
        grouped.set(r.avatar_id, existing)
      }
      const rules: SkuRule[] = Array.from(grouped.entries()).map(([avatarId, prefixes]) => ({
        id: `rule-${avatarId}`,
        avatarId,
        prefixes,
      }))
      setSkuRules(rules)
    }).catch(handleApiError)
  }, [])

  const handleJobCreated = (job: Job) => {
    setJobs(prev => [job, ...prev])
  }

  const handleConfigure = (jobId: string) => {
    setSelectedJobId(jobId)
    navTo('batches')
  }

  const handleUpdateJobs = (ids: string[], status: Job['status']) => {
    setJobs(prev => prev.map(j => ids.includes(j.id) ? { ...j, status } : j))
  }

  const handleDeleteJobs = (ids: string[]) => {
    setJobs(prev => prev.filter(j => !ids.includes(j.id)))
    if (selectedJobId && ids.includes(selectedJobId)) setSelectedJobId(null)
  }


  if (maintenanceMode) return <MaintenancePage onResolved={() => applyMaintenance(false)} onLogout={() => { applyMaintenance(false); onBack() }} />

  return (
    <SidebarProvider style={{ minHeight: '100svh' }}>
      <CliSidebar active={view} onNav={v => { navTo(v); setBgProgress(null) }} onLogout={onBack} onNewBatch={() => { navTo('new-batch'); setBgProgress(null) }} jobs={jobs} selectedJobId={selectedJobId} onSelectJob={id => { setSelectedJobId(id || null); if (id) navTo('batches') }} userEmail={getStoredUser().email} tenantName={getStoredUser().tenantName} onNavCatalogue={catId => { navToCatalogue(catId); setBgProgress(null) }} enabledModules={enabledModules} credits={credits} />
      <SidebarInset>
        <header className="flex h-12 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-4" />
          {(view === 'batches' || view === 'batches-done') && selectedJobId ? (
            <span className="text-sm font-semibold flex items-center gap-1.5">
              <button onClick={() => setSelectedJobId(null)} className="text-muted-foreground hover:text-foreground transition-colors">Jobs</button>
              <span className="text-muted-foreground">/</span>
              <span>{jobs.find(j => j.id === selectedJobId)?.name ?? ''}</span>
            </span>
          ) : (
            <span className="text-sm font-semibold">
              {view === 'settings' ? 'Settings' : view === 'new-batch' ? 'New Batch' : view === 'batches-done' ? 'Finished' : view === 'catalogue' ? 'Data' : 'Jobs'}
            </span>
          )}
          {bgProgress !== null && bgProgress < 100 && (
            <span className="ml-auto text-xs text-muted-foreground">Uploading… {bgProgress}%</span>
          )}
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          {view === 'settings'   && <CliSettings cloud={cloud} onCloudChange={setCloud} skuRules={skuRules} onSkuRulesChange={setSkuRules} avatars={availableAvatars} />}
          {view === 'new-batch'  && <NewBatchPage onBackground={setBgProgress} cloud={cloud} onGoSettings={() => navTo('settings')} onJobCreated={handleJobCreated} onConfigure={handleConfigure} />}
          {view === 'catalogue'  && enabledModules?.includes('catalogue') && <CataloguePage initialCatalogueId={selectedCatalogueId} />}
          {(view === 'batches' || view === 'batches-done') && <JobsView jobs={jobs} selectedId={selectedJobId} onSelect={id => setSelectedJobId(id || null)} onNewBatch={() => { navTo('new-batch'); setBgProgress(null) }} onUpdateJobs={handleUpdateJobs} onPatchJob={(id, patch) => {
                const prev = jobs.find(j => j.id === id)
                setJobs(js => js.map(j => j.id === id ? { ...j, ...patch } : j))
                // status/progress are server-owned — the client sends intents only
                const actionMap = { paused: 'pause', cancelled: 'cancel', not_configured: 'mark_not_configured' } as const
                const apiBody = {
                  action: patch.status ? actionMap[patch.status as keyof typeof actionMap] : undefined,
                  name: patch.name,
                  output_format: patch.outputFormat,
                  output_resolution: patch.outputResolution,
                  naming_convention: patch.namingConvention,
                  naming_custom: patch.namingCustom,
                }
                // Server-status echoes (e.g. 'queued' from /start) are local-only
                if (Object.values(apiBody).some(v => v !== undefined)) {
                  jobsApi.update(Number(id), apiBody as Parameters<typeof jobsApi.update>[1]).catch(() => {
                    if (prev) setJobs(js => js.map(j => j.id === id ? prev : j))
                  })
                }
              }} onDeleteJobs={handleDeleteJobs} availableAvatars={availableAvatars} skuProperties={skuProperties} skuRules={skuRules} filter={view === 'batches-done' ? 'done' : 'active'} cloud={cloud} />}
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

// ── Admin DTO mappers ─────────────────────────────────────────────────────
function dtoToClient(t: TenantDTO): Client {
  return {
    id: t.id,
    name: t.name,
    email: t.email,
    status: t.status as Client['status'],
    storage: { used: t.storage_used_gb ?? 0, temp: 0, total: t.storage_quota_gb },
    joined: '',
    avatarProduct: t.avatar_product,
    avatars: t.avatar_ids ?? [],
    studioApi: t.studio_api,
    credits: { used: t.credits_used, budget: t.credits_budget },
  }
}


function dtoToFolder(f: AvatarFolderDTO): AvatarFolder {
  return { id: String(f.id), name: f.name, parentId: f.parent_id ? String(f.parent_id) : null, private: f.is_private }
}

function dtoToAsset(a: AvatarAssetDTO): AvatarAsset {
  return { id: String(a.id), name: a.name, img: a.img_path, folderId: a.folder_id ? String(a.folder_id) : null, private: a.is_private }
}

// ── Admin ─────────────────────────────────────────────────────────────────
const adminTitles: Record<string, string> = {
  dashboard:'Dashboard', clients:'Clients', avatars:'Avatars', api:'API Settings', vps:'VPS Monitor', admins:'Admin Users', modules:'Modules', debug:'Debug',
}

function AdminApp({ onBack }: { onBack: () => void }) {
  const [view, setView] = useState(() => localStorage.getItem("admin_view") ?? "dashboard")
  const navTo = (v: string) => { setView(v); localStorage.setItem("admin_view", v) }
  const [clients, setClients] = useState<Client[]>([])
  const [folders, setFolders] = useState<AvatarFolder[]>([])
  const [assets, setAssets] = useState<AvatarAsset[]>([])
  const [assetDTOs, setAssetDTOs] = useState<AvatarAssetDTO[]>([])
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [adminUsers, setAdminUsers] = useState<AdminUserDTO[]>([])
  const [currentAdminId, setCurrentAdminId] = useState<number>(0)
  const [modules, setModules] = useState<Module[]>([])

  useEffect(() => {
    adminClientsApi.list().then(dtos => setClients(dtos.map(dtoToClient))).catch(() => {})
    adminAvatarsApi.listFolders().then(dtos => setFolders(dtos.map(dtoToFolder))).catch(() => {})
    adminAvatarsApi.listAssets().then(dtos => { setAssets(dtos.map(dtoToAsset)); setAssetDTOs(dtos) }).catch(() => {})
    adminUsersApi.list().then(dtos => {
      setAdminUsers(dtos)
      const storedId = Number(localStorage.getItem("user_id"))
      if (storedId) setCurrentAdminId(storedId)
    }).catch(() => {})
    adminModulesApi.list().then(setModules).catch(() => {})
  }, [])

  const createClient = async (body: { name: string; email: string; password: string; storage_quota_gb: number; credits_budget: number }) => {
    const dto = await adminClientsApi.create(body)
    setClients(cs => [dtoToClient(dto), ...cs])
  }

  const updateClient = async (id: number, patch: Partial<Client>) => {
    setClients(cs => cs.map(c => c.id === id ? { ...c, ...patch } : c))
    await adminClientsApi.update(id, {
      name: patch.name,
      status: patch.status,
      storage_quota_gb: patch.storage?.total,
      credits_budget: patch.credits?.budget,
      avatar_product: patch.avatarProduct,
      studio_api: patch.studioApi,
    }).catch(() => {})
  }

  const deleteClient = async (id: number) => {
    await adminClientsApi.remove(id)
    setClients(cs => cs.filter(c => c.id !== id))
    setSelectedClient(null)
  }


  return (
    <SidebarProvider>
      <AdminSidebar active={view} onNav={v => { navTo(v); setSelectedClient(null) }} onLogout={onBack} counts={{ clients: clients.length }} />
      <SidebarInset>
        <header className="flex h-12 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-4" />
          {view === 'clients' && selectedClient ? (
            <span className="text-sm font-semibold flex items-center gap-1.5">
              <button onClick={() => setSelectedClient(null)} className="text-muted-foreground hover:text-foreground transition-colors">Clients</button>
              <span className="text-muted-foreground">/</span>
              <span>{selectedClient.name}</span>
            </span>
          ) : (
            <span className="text-sm font-semibold">{adminTitles[view] ?? "Admin"}</span>
          )}
          {view === 'clients' && !selectedClient && (
            <Button size="sm" className="ml-auto rounded-full" onClick={() => navTo('clients-new')}>+ New client</Button>
          )}
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          {view === 'dashboard' && <AdminDashboard clients={clients} />}
          {view === 'clients' && !selectedClient && <ClientsView clients={clients} onSelect={setSelectedClient} />}
          {view === 'clients' && selectedClient && <ClientDetail client={selectedClient} clients={clients} assets={assetDTOs} onBack={() => setSelectedClient(null)} onUpdate={updateClient} onDelete={deleteClient} />}
          {view === 'clients-new' && <NewClientForm onCancel={() => navTo('clients')} onCreate={async (body) => { await createClient(body); navTo('clients') }} />}
          {view === 'avatars' && <AvatarsPage folders={folders} assets={assets} onFoldersChange={setFolders} onAssetsChange={setAssets} />}
          {view === 'api' && <APISettings />}
          {view === 'vps' && <VPSMonitor />}
          {view === 'admins' && <AdminUsersPage users={adminUsers} currentUserId={currentAdminId} onUsersChange={setAdminUsers} />}
          {view === 'modules' && <ModulesPage modules={modules} onModulesChange={setModules} />}
          {view === 'debug'   && <DebugPage />}
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState<Page>(() => {
    const role = getStoredRole()
    if (role === "admin") return "admin"
    if (role === "client") return "cli"
    return "login"
  })

  useEffect(() => {
    const handler = () => {
      localStorage.removeItem("cli_view")
      localStorage.removeItem("admin_view")
      setPage("login")
    }
    window.addEventListener("session-expired", handler)
    return () => window.removeEventListener("session-expired", handler)
  }, [])

  function handleLogout() {
    clearTokens()
    localStorage.removeItem("cli_view")
    localStorage.removeItem("admin_view")
    setPage("login")
  }

  return (
    <TooltipProvider>
      <Toaster richColors closeButton position="bottom-right" />
      {page === "login" && <Login onNavigate={setPage} />}
      {page === "cli"   && <ErrorBoundary><CliApp   onBack={handleLogout} /></ErrorBoundary>}
      {page === "admin" && <ErrorBoundary><AdminApp onBack={handleLogout} /></ErrorBoundary>}
    </TooltipProvider>
  )
}
